// /app/frontend/src/components/frugal-v2/components/ZenCard.jsx
// Zen Daily y Vital Daily display card (130 lines)

import React, { useState } from 'react';
import { Sparkles, Calendar, Shield, Utensils } from 'lucide-react';
import ZenChatModal from './ZenChatModal';

const ZenCard = ({ zen, token, t, language = 'es', incomes = [], expenses = [], savings = [], transactions = [], accounts = {} }) => {
  const [showChat, setShowChat] = useState(false);
  
  // Build financial context for Maestro ZEN
  const financialContext = {
    zen,
    incomes,
    expenses,
    savings,
    transactions,
    accounts
  };
  
  if (!zen) return null;

  const { 
    zen_daily, 
    zen_vital_daily, 
    contingency_balance,
    contingency_budget,
    contingency_used,
    contingency_percent,
    days_remaining, 
    totals 
  } = zen;

  const isZenDepleted = zen?.zen_is_exhausted ?? (zen_daily <= 0);
  // STRICT: contingency UI only highlights when Daily Zen is actually exhausted
  // and contingency has begun to drain. Historical records alone do NOT trigger it.
  const isContingencyUsed = zen?.contingency_being_used ?? false;
  const isVitalBeingConsumed = zen?.vital_is_being_consumed || false;

  return (
    <>
      <div className="space-y-3">
        {/* Two main cards side by side */}
        <div className="grid grid-cols-2 gap-3">
          {/* Vital Daily Card - Para vivir */}
          <div className={`bg-gradient-to-br rounded-xl p-3 border ${
            isVitalBeingConsumed
              ? 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30'
              : 'from-sky-500/20 to-sky-600/10 border-sky-500/20'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                isVitalBeingConsumed ? 'bg-yellow-500/20' : 'bg-sky-500/20'
              }`}>
                <Utensils className={`w-3 h-3 ${isVitalBeingConsumed ? 'text-yellow-400' : 'text-sky-400'}`} />
              </div>
              <span className={`text-xs font-medium uppercase tracking-wider ${
                isVitalBeingConsumed ? 'text-yellow-400' : 'text-sky-400'
              }`}>
                {t('frugal.dashboard.vitalDaily')}
              </span>
            </div>
            <div className={`text-xl font-bold mb-0.5 ${isVitalBeingConsumed ? 'text-yellow-400' : 'text-white'}`}>
              ${zen_vital_daily?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-slate-400">
              {t('frugal.dashboard.vitalDailyDesc')}
            </div>
            {isVitalBeingConsumed && (
              <div className="mt-1 text-[10px] text-yellow-400 font-medium">
                {t('frugal.dashboard.vitalBeingUsedWarning')}
              </div>
            )}
            {/* Vital Remaining dentro de la tarjeta */}
            <div className={`mt-2 pt-2 border-t flex justify-between items-center ${
              isVitalBeingConsumed ? 'border-yellow-500/20' : 'border-sky-500/20'
            }`}>
              <span className="text-xs text-slate-500">{t('frugal.dashboard.vitalRemaining')}:</span>
              <span className={`text-sm font-medium ${isVitalBeingConsumed ? 'text-yellow-400' : 'text-sky-400'}`}>
                ${totals?.vital_remaining?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          {/* Zen Daily Card - Para otras cosas */}
          <div className={`bg-gradient-to-br rounded-xl p-3 border ${
            isZenDepleted 
              ? 'from-red-500/20 to-red-600/10 border-red-500/30' 
              : 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/20'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                isZenDepleted ? 'bg-red-500/20' : 'bg-emerald-500/20'
              }`}>
                <Sparkles className={`w-3 h-3 ${isZenDepleted ? 'text-red-400' : 'text-emerald-400'}`} />
              </div>
              <span className={`text-xs font-medium uppercase tracking-wider ${
                isZenDepleted ? 'text-red-400' : 'text-emerald-400'
              }`}>
                {t('frugal.dashboard.zenDaily')}
              </span>
            </div>
            <div className={`text-xl font-bold mb-0.5 ${isZenDepleted ? 'text-red-400' : 'text-white'}`}>
              ${zen_daily?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className={`text-[10px] uppercase tracking-wider mb-1 ${
              isZenDepleted ? 'text-red-300/60' : 'text-emerald-300/60'
            }`}>
              {t('frugal.dashboard.zenDailySubtitle')}
            </div>
            <div className="text-xs text-slate-400">
              {t('frugal.dashboard.zenDailyDesc')}
            </div>
            {/* Available dentro de la tarjeta */}
            <div className={`mt-2 pt-2 border-t flex justify-between items-center ${
              isZenDepleted ? 'border-red-500/20' : 'border-emerald-500/20'
            }`}>
              <span className="text-xs text-slate-500">{t('frugal.dashboard.available')}:</span>
              <span className={`text-sm font-medium ${isZenDepleted ? 'text-red-400' : 'text-emerald-400'}`}>
                ${totals?.available?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Bottom row - Days and Contingency with Little Zen */}
        <div className="bg-slate-800/50 rounded-xl p-3 border border-white/5">
          <div className="flex items-center justify-between">
            {/* Days Remaining */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <div className="text-xl font-bold text-white">{days_remaining}</div>
                <div className="text-xs text-slate-500">{t('frugal.dashboard.daysLeft')}</div>
              </div>
            </div>

            {/* Little Zen Robot - CLICKABLE */}
            <button 
              onClick={() => setShowChat(true)}
              className="flex-shrink-0 hover:scale-110 transition-transform cursor-pointer group"
              data-testid="little-zen-chat-btn"
            >
              <img 
                src="/little-zen-frugal.png" 
                alt="Maestro ZEN" 
                className="w-14 h-14 object-contain drop-shadow-lg group-hover:drop-shadow-2xl"
              />
              <span className="text-[10px] text-emerald-400 block text-center mt-0.5">
                {t('frugal.chat.askMe')}
              </span>
            </button>

            {/* Contingency Fund */}
            <div className="flex items-center gap-3">
              <div>
                <div className={`text-xl font-bold text-right ${
                  isContingencyUsed ? 'text-yellow-400' : 'text-amber-400'
                }`}>
                  ${contingency_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-slate-500 text-right">
                  {t('frugal.dashboard.contingency')} ({contingency_percent}%)
                </div>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Modal */}
      <ZenChatModal 
        isOpen={showChat} 
        onClose={() => setShowChat(false)} 
        token={token}
        t={t}
        language={language}
        financialContext={financialContext}
      />
    </>
  );
};

export default ZenCard;
